import { useTranslation } from "react-i18next";
import { Alert } from "@/atoms/alert";
import { Button } from "@/atoms/button";
import { BrandBar } from "@/components/brand-bar";
import { StripeLine } from "@/components/stripe-line";

/**
 * The door (template auth 1.16, the sent state).
 *
 * One 400 card on paper soft: a heading, one line saying what will happen, and one orange
 * button. Auth never confirms that an account exists, so the sent sentence is the same whatever
 * the address and there is no field to type one into: the page already knows whose billing this
 * URL points at, and asking would be asking a person to guess their coach's spelling of their
 * own email.
 *
 * Nothing on the card itself carries a mark; the brand bar sits above it and the one line
 * underneath says who secures the money.
 */

export interface PortalDoorProps {
  sending: boolean;
  /** The masked address the API answers with; only ever shown after a send. */
  sentTo: string | null;
  failed: boolean;
  onSend: () => void;
}

export function PortalDoor({
  sending,
  sentTo,
  failed,
  onSend,
}: PortalDoorProps) {
  const { t } = useTranslation();

  return (
    <div className="bg-surface-paperSoft px-gk-4 py-gk-4 sm:py-gk-8 flex min-h-screen flex-col items-center">
      <div className="gap-gk-4 flex w-full max-w-[400px] flex-col">
        {/* The brand bar, on the door too.
            auth 1.16 keeps Gymkee off a sign-in door, and that rule is about not making a person
            prove themselves to a logo. This door is a money door: the thing on the other side of
            it is somebody's card and their subscriptions, and Mo's call is that the product says
            its own name before it asks for an email. Recorded as a divergence from auth 1.16. */}
        <div className="gap-gk-4 sm:gap-gk-6 flex flex-col">
          <BrandBar />
          <main className="ds-reveal ds-card ds-hairline bg-surface-paper p-gk-6 gap-gk-4 flex flex-col">
            <div className="gap-gk-2 flex flex-col">
              <h1 className="text-[24px] leading-[1.2] font-semibold text-[var(--gk-text-primary)]">
                {t("gymkeePay.portal.doorTitle")}
              </h1>
              <p className="text-[15px] leading-[1.55] text-[var(--gk-text-secondary)]">
                {t("gymkeePay.portal.doorLine")}
              </p>
            </div>

            {sentTo && (
              <Alert tone="success">
                {t("gymkeePay.portal.doorSent", { email: sentTo })}
              </Alert>
            )}
            {failed && (
              <Alert tone="danger">{t("gymkeePay.portal.doorFailed")}</Alert>
            )}

            {sentTo ? (
              <Button
                variant="tertiary"
                loading={sending}
                onClick={onSend}
                className="self-start"
              >
                {t("gymkeePay.portal.doorResend")}
              </Button>
            ) : (
              <Button
                variant="primary"
                size="large"
                fullWidth
                loading={sending}
                onClick={onSend}
              >
                {t("gymkeePay.portal.doorAction")}
              </Button>
            )}
          </main>
        </div>
        <StripeLine />
      </div>
    </div>
  );
}
